import { fetchProvider } from 'actions/providers'
import { fetchRequest } from 'actions/requests'
import { fetchService } from 'actions/services'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { Request } from 'hooks/requestHooks'
import { Provider } from 'interfaces/Providers'
import { Reclaim } from 'interfaces/Reclaims'
import Service from 'interfaces/Services'
import { useEffect, useMemo } from 'react'

export interface ReclaimTaskData {
    request: Request | null
    service: Service | null
    provider: Provider | null
    isLoading: boolean
    hasData: boolean
}

/**
 * Optimized hook for fetching all data needed for a reclaim task overview
 * This hook batches data fetching and reduces re-renders by consolidating
 * multiple API calls into a single effect and memoizing the results.
 */
export const useReclaimTaskData = (reclaim: Reclaim): ReclaimTaskData => {
    const dispatch = useAppDispatch()

    // Select all data from Redux store in one go
    const { request, service, provider, isRequestFetching, isServiceFetching, isProviderFetching } =
        useAppSelector((state) => {
            const requestData = reclaim.requestId
                ? state.requests.entities.requests?.[reclaim.requestId]
                : null
            const serviceData = reclaim.serviceId
                ? state.services.entities.services?.[reclaim.serviceId]
                : null
            const providerData = reclaim.providerId
                ? state.providers.entities.providers?.[reclaim.providerId]
                : null

            return {
                request: requestData,
                service: serviceData,
                provider: providerData,
                isRequestFetching: state.requests.isFetching,
                isServiceFetching: state.services.isFetching,
                isProviderFetching: state.providers.isFetching,
            }
        })

    // Memoized loading state calculation
    const isLoading = useMemo(
        () => isRequestFetching || isServiceFetching || isProviderFetching,
        [isRequestFetching, isServiceFetching, isProviderFetching],
    )

    // Memoized data availability check
    const hasData = useMemo(
        () => Boolean(request && service && provider),
        [request, service, provider],
    )

    // Batch all data fetching into a single effect
    useEffect(() => {
        const fetchData = async () => {
            const promises = []

            // Only fetch request if we don't have it or it's missing required data
            if (reclaim.requestId && (!request || !request.requests_options)) {
                promises.push(dispatch(fetchRequest(reclaim.requestId, 'default', false)))
            }

            // Only fetch service if we don't have it
            if (reclaim.serviceId && !service) {
                promises.push(dispatch(fetchService(reclaim.serviceId)))
            }

            // Only fetch provider if we don't have it
            if (reclaim.providerId && !provider) {
                promises.push(dispatch(fetchProvider(reclaim.providerId, false)))
            }

            // Execute all fetches in parallel if there are any
            if (promises.length > 0) {
                await Promise.all(promises)
            }
        }

        fetchData()
    }, [
        dispatch,
        reclaim.requestId,
        reclaim.serviceId,
        reclaim.providerId,
        request?.requests_options,
        service,
        provider,
    ])

    // Memoized return value to prevent unnecessary re-renders
    return useMemo(
        () => ({
            request,
            service,
            provider,
            isLoading,
            hasData,
        }),
        [request, service, provider, isLoading, hasData],
    )
}
